/**
 * Состав заказа по экологии (ecology-order): услуга, площадки с реквизитами из заявки
 * (ecology-order-fields) и результатом, ответы на вопросы при заказе и комментарий менеджеру. Один компонент для карточки заказа у клиента и у команды —
 * отличается только источником данных (`api`) и подписями «фотографии».
 */
import { useEffect, useState } from 'react'
import { MapPin, Paperclip } from 'lucide-react'
import type { EcoOrder } from '@/lib/api'
import { getErrorMessage } from '@/lib/errors'
import { ECO_KIND_SHORT_LABELS, formatEcoDate } from '@/lib/ecology'
import { Card } from '@/design-system/components/layout/card'
import { Alert } from '@/design-system/components/feedback/alert'

/** Загрузка заказа по экологии (клиент или команда) — общий хук карточек заказа. */
export function useEcoOrder(
  load: (signal?: AbortSignal) => Promise<EcoOrder>,
  version = 0,
): { ecoOrder: EcoOrder | null; error: string | null; setEcoOrder: (o: EcoOrder) => void } {
  const [ecoOrder, setEcoOrder] = useState<EcoOrder | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    load(controller.signal)
      .then((o) => {
        if (!controller.signal.aborted) setEcoOrder(o)
      })
      .catch((err) => {
        if (!controller.signal.aborted) setError(getErrorMessage(err, 'Не удалось загрузить заказ'))
      })
    return () => controller.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [version])

  return { ecoOrder, error, setEcoOrder }
}

export function EcoOrderSummary({ ecoOrder }: { ecoOrder: EcoOrder }) {
  const name = ecoOrder.snapshotName || ecoOrder.item?.name || 'Услуга не выбрана'
  const note = ecoOrder.snapshotNote || ecoOrder.item?.clientDescription || ''
  return (
    <div className="flex flex-col gap-4">
      <Card header="Услуга">
        <div className="font-medium text-txt">{name}</div>
        <div className="text-13 text-txt-secondary">
          {[ecoOrder.item ? ECO_KIND_SHORT_LABELS[ecoOrder.item.kind] : '', note]
            .filter(Boolean)
            .join(' · ')}
        </div>
      </Card>

      <Card header={`Площадки · ${ecoOrder.sites.length}`}>
        {ecoOrder.sites.length === 0 ? (
          <p className="text-13 text-txt-secondary">Площадки не выбраны.</p>
        ) : (
          <ul>
            {ecoOrder.sites.map((site) => (
              <li
                key={site.id}
                className="flex flex-wrap items-start gap-x-4 gap-y-1 border-b border-line py-2.5 last:border-b-0"
              >
                <span className="flex min-w-0 flex-1 items-start gap-2">
                  <MapPin
                    className="mt-0.5 size-4 shrink-0 stroke-current text-txt-tertiary"
                    aria-hidden
                  />
                  <span className="min-w-0">
                    <span className="block font-medium text-txt">{site.name}</span>
                    {site.address ? (
                      <span className="block text-12 text-txt-secondary">{site.address}</span>
                    ) : null}
                    {site.snapshotValues.map((v) => (
                      <span key={v.label} className="block text-12 text-txt-secondary">
                        {v.label}:{' '}
                        {v.files?.length ? (
                          // Файлы заявки (ecology-order-fields-files-fkko) — ссылками на скачивание.
                          v.files.map((f, i) => (
                            <span key={f.id}>
                              {i > 0 ? ', ' : null}
                              <a href={f.url} className="text-accent hover:underline">
                                {f.name}
                              </a>
                            </span>
                          ))
                        ) : (
                          <span className="text-txt">{v.value}</span>
                        )}
                      </span>
                    ))}
                  </span>
                </span>
                <span className="text-13 text-txt-secondary">
                  {site.resultDate ? (
                    <>
                      готово {formatEcoDate(site.resultDate)}
                      {site.resultValidUntil ? ` · до ${formatEcoDate(site.resultValidUntil)}` : ''}
                    </>
                  ) : (
                    'в работе'
                  )}
                </span>
                {site.resultFileUrl ? (
                  <a
                    href={site.resultFileUrl}
                    className="inline-flex max-w-48 items-center gap-1 text-13 text-accent hover:underline"
                    title={site.resultFileName}
                  >
                    <Paperclip className="size-4 shrink-0 stroke-current" aria-hidden />
                    <span className="truncate">{site.resultFileName}</span>
                  </a>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </Card>

      {ecoOrder.snapshotAnswers.length > 0 || ecoOrder.comment ? (
        <Card header="Ответы и комментарий">
          <ul className="flex flex-col gap-2">
            {ecoOrder.snapshotAnswers.map((a) => (
              <li key={a.question}>
                <div className="text-13 text-txt-secondary">{a.question}</div>
                <div className="font-medium text-txt">{a.answer || '—'}</div>
              </li>
            ))}
            {ecoOrder.comment ? (
              <li>
                <div className="text-13 text-txt-secondary">Комментарий менеджеру</div>
                <div className="text-txt">{ecoOrder.comment}</div>
              </li>
            ) : null}
          </ul>
        </Card>
      ) : null}
    </div>
  )
}

/** Сообщение об ошибке загрузки заказа по экологии. */
export function EcoOrderError({ error }: { error: string }) {
  return <Alert variant="danger">{error}</Alert>
}
